import { useSession } from '@zhiyue/core';
import { Alert, Button, Spin } from 'antd';
import React from 'react';
import { portfolioApi } from '@/services/bff/portfolio.api';
import { hasOAuth2Session } from '@/services/oauth2';
import { createEmptyPortfolioState } from './emptyState';
import type { PortfolioState } from './types';

const PortfolioDataGate: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { apiReady, apiError, refreshFromApi } = usePortfolioStore();
  const { currentUser, loading: sessionLoading } = useSession();

  if (
    !sessionLoading &&
    hasOAuth2Session() &&
    currentUser &&
    apiError &&
    apiReady
  ) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="投后数据加载失败"
          description={`${apiError}，已展示可用部分，请重试。`}
          action={
            <Button size="small" onClick={() => void refreshFromApi()}>
              重试
            </Button>
          }
        />
      </div>
    );
  }

  if (!sessionLoading && hasOAuth2Session() && currentUser && !apiReady) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
        }}
      >
        <Spin />
      </div>
    );
  }

  return <>{children}</>;
};

const PortfolioContext = React.createContext<{
  state: PortfolioState;
  apiReady: boolean;
  apiError: string | null;
  refreshFromApi: () => Promise<void>;
} | null>(null);

export const PortfolioStoreProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const [state, setState] = React.useState(createEmptyPortfolioState);
  const [apiReady, setApiReady] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);

  const refreshFromApi = React.useCallback(async () => {
    const results = await Promise.allSettled([
      portfolioApi.listCompanies(),
      portfolioApi.listRelations(),
      portfolioApi.listOpportunities(),
      portfolioApi.listSnapshots(),
      portfolioApi.listIssues(),
      portfolioApi.listRisks(),
      portfolioApi.listReports(),
    ]);
    const [
      companies,
      investments,
      opportunities,
      snapshots,
      issues,
      risks,
      reports,
    ] = results.map(
      (r) => (r.status === 'fulfilled' ? r.value : []),
    ) as [
      PortfolioState['companies'],
      PortfolioState['investments'],
      PortfolioState['opportunities'],
      PortfolioState['snapshots'],
      PortfolioState['issues'],
      PortfolioState['risks'],
      PortfolioState['reports'],
    ];
    const failedCount = results.filter(
      (r) => r.status === 'rejected',
    ).length;
    setApiError(failedCount ? `部分接口失败（${failedCount}/7）` : null);
    setState((current) => ({
      ...current,
      companies,
      investments,
      opportunities,
      snapshots,
      issues,
      risks,
      reports,
    }));
    setApiReady(true);
  }, []);

  React.useEffect(() => {
    if (sessionLoading || !hasOAuth2Session() || !currentUser) {
      return;
    }
    void refreshFromApi();
  }, [refreshFromApi, sessionLoading, currentUser]);

  const value = React.useMemo(
    () => ({ state, apiReady, apiError, refreshFromApi }),
    [state, apiReady, apiError, refreshFromApi],
  );
  return (
    <PortfolioContext.Provider value={value}>
      <PortfolioDataGate>{children}</PortfolioDataGate>
    </PortfolioContext.Provider>
  );
};

export const usePortfolioStore = () => {
  const context = React.useContext(PortfolioContext);
  if (!context)
    throw new Error(
      'usePortfolioStore must be used inside PortfolioStoreProvider',
    );
  return context;
};
